import type { User } from '../../../authentication/types/types.auth';
import {
  Edit,
  ThumbDown,
  ThumbUp,
  TrendingUp,
  Visibility,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { commonCards, gradients } from '@shared/theme/patterns';

// Minimal user type for ProfileHeader
interface ProfileHeaderUser {
  id?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatarColor?: string;
  tagLine?: string;
  reviews?: unknown[];
}

interface ProfileHeaderProps {
  user: User | ProfileHeaderUser;
  isEditing: boolean;
  reactionStats: {
    totalLikes: number;
    totalDislikes: number;
    loading: boolean;
  };
  onEditClick?: () => void;
  avatarColor?: string;
  totalReviews?: number;
}

// Helper function to generate avatar initials from username or name
function getAvatarInitials(user: ProfileHeaderUser): string {
  // If username exists, use it
  if (user.username) {
    // Split by capital letters to detect multi-word usernames (e.g., "BoldPhoenix" -> ["Bold", "Phoenix"])
    const words = user.username.match(/[A-Z][a-z]+/g) || [];

    if (words.length >= 2) {
      // Multi-word: take first letter of first two words (e.g., "BoldPhoenix" -> "BP")
      return (words[0]?.[0] || '') + (words[1]?.[0] || '');
    }
    else {
      // Single word: take first two letters (e.g., "Phoenix" -> "PH")
      return user.username.substring(0, 2).toUpperCase();
    }
  }

  // Fallback: use first and last name initials
  return (user.firstName?.[0] || '') + (user.lastName?.[0] || '');
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  isEditing,
  reactionStats,
  onEditClick,
  avatarColor,
  totalReviews,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/user/${user.username}`);
  };

  const stats = [
    {
      icon: <ThumbUp sx={{ fontSize: 32, color: '#4ade80' }} />,
      label: 'Helpful Reviews',
      value: reactionStats.loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : reactionStats.totalLikes,
    },
    {
      icon: <ThumbDown sx={{ fontSize: 32, color: '#f87171' }} />,
      label: 'Not Helpful',
      value: reactionStats.loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : reactionStats.totalDislikes,
    },
    {
      icon: <TrendingUp sx={{ fontSize: 32, color: '#fbbf24' }} />,
      label: 'Published Reviews',
      value:
        totalReviews !== undefined ? totalReviews : user.reviews?.length || 0,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      data-testid="profile-header"
    >
      <Paper
        sx={{
          ...commonCards.bordered,
          background: gradients.primary,
          color: 'white',
          mb: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Decorative background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '300px',
            background:
              'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            transform: 'translate(30%, -30%)',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                bgcolor: avatarColor || user.avatarColor || '#c05a3a',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 700,
                border: '4px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                transition: 'background-color 0.3s ease',
              }}
            >
              {getAvatarInitials(user)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {user.username || `${user.firstName} ${user.lastName}`}
              </Typography>
              {user.tagLine && (
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    fontStyle: 'italic',
                    fontSize: '1.125rem',
                  }}
                >
                  {user.tagLine}
                </Typography>
              )}
            </Box>

            {!isEditing && onEditClick && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Tooltip title="View Public Profile">
                  <IconButton
                    onClick={handleViewProfile}
                    sx={{
                      'bgcolor': 'rgba(255,255,255,0.2)',
                      'color': 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                        transform: 'scale(1.1)',
                      },
                      'transition': 'all 0.3s ease',
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Profile">
                  <IconButton
                    onClick={onEditClick}
                    data-testid="edit-profile-button"
                    sx={{
                      'bgcolor': 'rgba(255,255,255,0.2)',
                      'color': 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                        transform: 'scale(1.1)',
                      },
                      'transition': 'all 0.3s ease',
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Stats Row */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {stats.map((stat, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 11px)' },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.4 }}
                >
                  <Card
                    sx={{
                      'background': 'rgba(255,255,255,0.15)',
                      'backdropFilter': 'blur(10px)',
                      'border': '1px solid rgba(255,255,255,0.2)',
                      'p': 2,
                      'textAlign': 'center',
                      'transition': 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.25)',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.9)' }}
                    >
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};
